import { useEffect } from 'react';
import { ACTION_TYPES, useMenuDispatch, useMenuState } from './menu-context';
import MenuAppService from '../../../../application/platform/apm/menu/menu-app-service';
import { IMenuResponse } from '../../../../model/apm/menu/response/menu-response';
import { IMenuItemResponse } from '../../../../model/apm/menu/response/menu-item-response';
import { IMenuAddEditRequest } from '../../../../model/apm/menu/request/menu-add-edit-request';
import { IMenuItemAddEditRequest } from '../../../../model/apm/menu/request/menu-item-add-edit-request';
import { reorderOptimistic } from '../../components/reorder-utils';
import { removeById, upsertById } from '../../../../utils/list-utils';
import { useActionRunner } from '../../../../hooks/use-action-runner';
import { buildVisibilityRequest } from '../components/menu-utils';

export const useMenuController = () => {
    const dispatch = useMenuDispatch();
    const state = useMenuState();
    const { isSaving, runSaving, run, showSuccess, confirmDelete } = useActionRunner();

    function setMenus(menus: IMenuResponse[]) {
        dispatch({ type: ACTION_TYPES.setMenus, payload: menus });
    }

    function setItems(items: IMenuItemResponse[]) {
        dispatch({ type: ACTION_TYPES.setItems, payload: items });
    }

    async function loadMenus(selectId?: number) {
        dispatch({ type: ACTION_TYPES.setLoadingMenus, payload: true });
        await run('Não foi possível carregar os menus', async () => {
            const menus = await MenuAppService.list();
            setMenus(menus);
            const nextSelected = selectId ?? state.selectedMenuId ?? menus.find((m) => m.canAddItems)?.id;
            if (nextSelected && menus.some((m) => m.id === nextSelected)) {
                await selectMenu(nextSelected);
                return;
            }
            dispatch({ type: ACTION_TYPES.selectMenu, payload: undefined });
        });
        dispatch({ type: ACTION_TYPES.setLoadingMenus, payload: false });
    }

    async function selectMenu(menuId: number) {
        dispatch({ type: ACTION_TYPES.selectMenu, payload: menuId });
        dispatch({ type: ACTION_TYPES.setLoadingItems, payload: true });
        await run('Não foi possível carregar os itens do menu', async () => {
            const menu = await MenuAppService.getById(menuId);
            setItems(menu.items || []);
        });
        dispatch({ type: ACTION_TYPES.setLoadingItems, payload: false });
    }

    useEffect(() => {
        loadMenus();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Menus
    function openMenuDrawer(menu?: IMenuResponse) {
        dispatch({ type: ACTION_TYPES.openMenuDrawer, payload: menu });
    }

    function closeMenuDrawer() {
        dispatch({ type: ACTION_TYPES.closeMenuDrawer });
    }

    function handleSaveMenu(request: IMenuAddEditRequest) {
        const editing = state.menuDrawer.menu;
        return runSaving('Não foi possível salvar o menu', async () => {
            const saved = await (editing ? MenuAppService.edit(editing.id, request) : MenuAppService.add(request));
            showSuccess(editing ? 'Menu atualizado com sucesso!' : 'Menu adicionado com sucesso!');
            closeMenuDrawer();
            await loadMenus(saved?.id ?? editing?.id);
        });
    }

    function handleToggleMenuVisibility(menu: IMenuResponse) {
        run('Não foi possível alterar a visibilidade do menu', async () => {
            const saved = await MenuAppService.edit(menu.id, buildVisibilityRequest(menu, !menu.isVisible, true));
            setMenus(upsertById(state.menus, { ...menu, ...saved, items: saved?.items ?? menu.items }));
            showSuccess(menu.isVisible ? 'Menu ocultado no portal.' : 'Menu exibido no portal.');
        });
    }

    function handleDeleteMenu(menu: IMenuResponse) {
        confirmDelete(`Tem certeza que deseja excluir o menu "${menu.title}" e todos os seus itens?`, () => run('Não foi possível excluir o menu', async () => {
            await MenuAppService.remove(menu.id);
            showSuccess('Menu excluído com sucesso!');
            await loadMenus(state.selectedMenuId === menu.id ? undefined : state.selectedMenuId);
        }));
    }

    // Barra principal (menus fixos) e "Mais opções" são reordenados separadamente; o Início fica sempre em primeiro
    function handleReorderMenus(next: IMenuResponse[]) {
        const isFixedGroup = next.some((m) => m.key);
        const ordered = [...next.filter((m) => !m.canReorder), ...next.filter((m) => m.canReorder)];
        const others = state.menus.filter((m) => !!m.key !== isFixedGroup);
        const merged = isFixedGroup ? [...ordered, ...others] : [...others, ...ordered];
        run('Não foi possível reordenar os menus', () => reorderOptimistic(
            state.menus, merged, (m) => m.id, setMenus,
            () => MenuAppService.reorder(ordered.filter((m) => m.canReorder).map((m) => m.id)),
        ));
    }

    // Itens
    function openItemDrawer(item?: IMenuItemResponse) {
        dispatch({ type: ACTION_TYPES.openItemDrawer, payload: item });
    }

    function closeItemDrawer() {
        dispatch({ type: ACTION_TYPES.closeItemDrawer });
    }

    function handleSaveItem(request: IMenuItemAddEditRequest) {
        const menuId = state.selectedMenuId;
        if (!menuId) return;
        const editing = state.itemDrawer.item;
        return runSaving('Não foi possível salvar o item', async () => {
            const saved = await (editing ? MenuAppService.editItem(menuId, editing.id, request) : MenuAppService.addItem(menuId, request));
            setItems(upsertById(state.items, saved));
            showSuccess(editing ? 'Item atualizado com sucesso!' : 'Item adicionado com sucesso!');
            closeItemDrawer();
        });
    }

    function handleToggleItemVisibility(item: IMenuItemResponse) {
        const menuId = state.selectedMenuId;
        if (!menuId) return;
        run('Não foi possível alterar a visibilidade do item', async () => {
            setItems(upsertById(state.items, await MenuAppService.editItem(menuId, item.id, buildVisibilityRequest(item, !item.isVisible, false))));
            showSuccess(item.isVisible ? 'Item ocultado no portal.' : 'Item exibido no portal.');
        });
    }

    function handleDeleteItem(item: IMenuItemResponse) {
        const menuId = state.selectedMenuId;
        if (!menuId) return;
        confirmDelete(`Tem certeza que deseja excluir o item "${item.title}"?`, () => run('Não foi possível excluir o item', async () => {
            await MenuAppService.removeItem(menuId, item.id);
            setItems(removeById(state.items, item.id));
            showSuccess('Item excluído com sucesso!');
        }));
    }

    function handleReorderItems(next: IMenuItemResponse[]) {
        const menuId = state.selectedMenuId;
        if (!menuId) return;
        run('Não foi possível reordenar os itens', () => reorderOptimistic(
            state.items, next, (i) => i.id, setItems,
            (ids) => MenuAppService.reorderItems(menuId, ids),
        ));
    }

    return {
        isSaving, selectMenu,
        openMenuDrawer, closeMenuDrawer, handleSaveMenu, handleToggleMenuVisibility, handleDeleteMenu, handleReorderMenus,
        openItemDrawer, closeItemDrawer, handleSaveItem, handleToggleItemVisibility, handleDeleteItem, handleReorderItems,
    };
};
